// app/ has no slice, so no model/ to hold hooks — colocate the hook next to
// the component per spec §4.3 (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md).
import { useSupportStore } from '@/entities/support';
import { useUserStore } from '@/entities/user';
import { EMITTER_CONSTANTS } from '@/shared/config';
import { useMergeState } from '@/shared/lib/customHooks';
import { emitter } from '@/shared/lib';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { getEscalatedMessage } from './modalEscalatedMessage.handler';

/**
 * Drives the billing escalation popup for the currently selected facility. Reads the support facilities and selected facility from the stores, opens the modal once on first load from that facility's arrears tier, listens for the show-popup-escalation event to open it on demand with an optional close callback, and exposes the modal state and its close handler.
 *
 * @returns The escalation modal state together with its close handler.
 */
export const useModalEscalatedMessage = () => {
  /**
   * Holds the list of support facilities read from the support store. Subscribes to the supportFacilities slice so the hook re-runs when that list changes.
   *
   * @returns The support facilities from the store.
   */
  const supportFacilitiesRedux = useSupportStore((s) => s.supportFacilities);
  /**
   * Holds the currently selected facility read from the user store. Subscribes to the selectedFacility slice so the hook re-runs when the selection changes.
   *
   * @returns The selected facility from the user store.
   */
  const selectedFacilityRedux = useUserStore((s) => s.selectedFacility);

  const isInitial = useRef(true);
  const closeCallback = useRef<(() => void) | undefined>(undefined);

  const [state, setState] = useMergeState<any>({
    isOpen: false,
    dataPopup: undefined,
  });

  /**
   * Dismisses the escalation modal and runs any callback registered by whoever opened it. Clears the open flag in state and invokes the stored close callback when one is present.
   *
   * @returns Nothing.
   */
  const onClose = () => {
    setState({ isOpen: false });
    if (closeCallback.current) {
      closeCallback.current();
    }
  };

  /**
   * Opens the escalation modal with the copy matching a facility's days-past-due tier. Returns early when no arrears value is given, otherwise reads the escalation message map and switches on the stringified value to pick the 30, 40, 50 or 60 day content, ignoring any other value.
   *
   * @param arrears - Days past due used to select the escalation tier.
   * @returns Nothing.
   */
  const handleResult = (arrears: any) => {
    if (!arrears) {
      return;
    }
    const escalatedMsg: any = getEscalatedMessage() || {};
    switch (arrears.toString()) {
      case '30': {
        setState({
          dataPopup: escalatedMsg?.DAY_30,
          isOpen: true,
        });
        break;
      }
      case '40': {
        setState({
          dataPopup: escalatedMsg?.DAY_40,
          isOpen: true,
        });
        break;
      }
      case '50': {
        setState({
          dataPopup: escalatedMsg?.DAY_50,
          isOpen: true,
        });
        break;
      }
      case '60': {
        setState({
          dataPopup: escalatedMsg?.DAY_60,
          isOpen: true,
        });
        break;
      }
      default: {
        break;
      }
    }
  };

  useEffect(() => {
    /**
     * Locates the support-facility record matching the currently selected facility. Searches the support facilities list from the store for the entry whose id equals the selected facility's value.
     *
     * @returns The matching support facility, or undefined.
     */
    const foundSelectedFacility = _.find(
      supportFacilitiesRedux,
      (x) => x.id === selectedFacilityRedux.value,
    );
    if (foundSelectedFacility) {
      // *: Show modal in first load time
      if (isInitial.current) {
        isInitial.current = false;
        handleResult(foundSelectedFacility.arrears);
      }
    }

    /**
     * Holds the emitter subscription that opens the escalation modal on demand. Listens for the show-popup-escalation event, opening the modal for the supplied arrears tier and storing or clearing the optional on-close callback.
     *
     * @returns The emitter listener handle, which is removed on cleanup.
     */
    const escalationListener = emitter.addListener(
      EMITTER_CONSTANTS.SHOW_POPUP_ESCALATION,
      (arrears: any, onClosePopup: any) => {
        handleResult(arrears);
        if (onClosePopup) {
          closeCallback.current = onClosePopup;
        } else {
          closeCallback.current = undefined;
        }
      },
    );
    return () => {
      escalationListener.remove();
    };
  }, [selectedFacilityRedux, supportFacilitiesRedux]);

  return { state, onClose };
};
